<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import IlluminatedButton from '@/components/buttons/IlluminatedButton.vue'
import OutlineButton from '@/components/buttons/OutlineButton.vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

gsap.registerPlugin(ScrollTrigger, SplitText)

const props = defineProps({
    title: String,
    content: String,
    buttonLabel: String,
    buttonLink: String,
    video: String,
    videoPoster: String
})

const videoRef = ref(null)
const containerRef = ref(null)
const titleRef = ref(null)
const contentRef = ref(null)
const buttonRef = ref(null)

// Map buttonType to component
const buttonComponents = {
    illuminated: IlluminatedButton,
    outline: OutlineButton,
}

const ButtonComponent = computed(() => {
    return buttonComponents[props.buttonType] || IlluminatedButton
})

let observer = null
let tl = null
let videoAnimation = null;

onMounted(async () => {
    if (videoRef.value) {
        observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    videoRef.value.play()
                } else {
                    videoRef.value.pause()
                }
            },
            { threshold: 0.5 }
        )
        observer.observe(videoRef.value)

        const videoContainer = containerRef.value.querySelector('.text-section__video-container');
        if (videoContainer) {
            videoAnimation = gsap.fromTo(videoContainer, 
                { yPercent: -10},
                {
                    yPercent: 5,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: containerRef.value,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 2
                    }
                }
            );
        }
    }

    const titleEl = titleRef.value
    const contentEl = contentRef.value
    const buttonEl = buttonRef.value

    let splitChars = []
    if (titleEl) {
        await document.fonts.ready;
        const split = new SplitText(titleEl, { type: 'words, chars' })
        splitChars = split.chars
    }

    gsap.set(splitChars, { opacity: 0, filter: 'blur(10px)', scale: 1.5 })
    if (contentEl) gsap.set(contentEl, { opacity: 0, filter: 'blur(10px)' })
    if (buttonEl) gsap.set(buttonEl, { opacity: 0, filter: 'blur(10px)' })

    tl = gsap.timeline({
        scrollTrigger: {
            trigger: containerRef.value,
            start: 'top center',
            end: 'bottom center',
        },
    })

    if (splitChars.length > 0) {
        tl.to(splitChars, {
            opacity: 1,
            filter: 'blur(0px)',
            scale: 1,
            stagger: 0.03,
            duration: 0.4,
            ease: 'power2.out',
        })
    }

    if (contentEl) {
        tl.to(contentEl, {
            opacity: 1,
            filter: 'blur(0px)',
            duration: 0.4,
            ease: 'power2.out',
        }, '<0.2')
    }

    if (buttonEl) {
        tl.to(buttonEl, {
            opacity: 1,
            filter: 'blur(0px)',
            duration: 0.4,
            ease: 'power2.out',
        }, '<0.1')
    }

    // Refresh ScrollTrigger after a short delay to ensure correct positioning
    setTimeout(() => {
        ScrollTrigger.refresh();
    }, 100);
})

onBeforeUnmount(() => {
    if (observer && videoRef.value) {
        observer.unobserve(videoRef.value)
        observer.disconnect()
    }
    if (tl) {
        tl.kill()
    }
    if (videoAnimation) {
        videoAnimation.kill();
    }
})
</script>

<template>
    <section class="video-text-item" ref="containerRef">
        <div class="main-content">
            <h2 v-if="title" class="text-section__title" ref="titleRef">{{ title }} </h2>
            <div v-if="content" class="text-section__content" v-html="content" ref="contentRef"></div>
            <div v-if="buttonLabel" ref="buttonRef">
                <component
                    class="text-section__button"
                    :is="ButtonComponent"
                    :label="buttonLabel"
                    :link="buttonLink"
                />
            </div>
        </div>
       

        <div class="text-section__video-container">
            <video 
                v-if="props.video" 
                ref="videoRef"
                :src="props.video"
                :poster="props.videoPoster"
                alt=""
                class="text-section__video" 
                muted
                playsinline
                loop
                preload="none"
                loading="lazy"
                />
        </div>
        
    </section>

</template>

<style lang="scss" scoped>
.video-text-item {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
    justify-content: center;
    align-items: center;
    max-width: 1575px;
    gap: 35px;
    position: relative;

    &.first::after{
       content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: 47vw;
        aspect-ratio: 9 / 8;
        transform: translate(-16%, -25%);
        z-index: -1;
        border-radius: 962px;
        opacity: 0.4;
        background: rgba(48, 162, 255, 0.35);
        filter: blur(250px);
    }


    & .main-content{
        order: 2;
        margin: 0 auto;
        max-width: 310px;
        text-align: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        & .text-section__button{
                margin: 0 auto;
        }
    }

    & .text-section__title{
        color: $white;
        background: unset;
        background-clip: initial;
        -webkit-background-clip: initial;
        -webkit-text-fill-color: initial;
    }

    & .text-section__content {
         margin: 30px 0 35px;
    }

    & .text-section__video-container{
        width: 100%;
        height: 100%;
        border-radius: 15px;
        overflow: hidden;
        position: relative;

         &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(307deg, rgba(0, 0, 0, 0.00) 34.93%, #000 116.66%);
            z-index: 1;
        }

        & .text-section__video {
            width: 100%;
            height: 100%;
            object-fit: cover;        
        }
    }
    
// Responsive
    @media (min-width: 767px) {
        grid-template-columns:  1fr 320px;
        gap: 200px;

        &.reverse {
            grid-template-columns: 320px 1fr;

            & .main-content {
                order: unset;
                
            }

            & .text-section__video-container::after{
                background: linear-gradient(44deg, rgba(0, 0, 0, 0.00) 45.89%, #000 114.58%);
            }
        }

        & .main-content{
            order: 2;
            align-items: flex-start;
            text-align: left;
            max-width: 330px;

            & .text-section__button{
                    margin: unset;
            }
        }

        & .text-section__video-container{
            border-radius: 25px;
        }
    }
}

</style>